import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../utils/supabase';
import { fetchCurrentUser, updateUserDarkMode, updateUserChatMarkdown, updateCachedSessionToken } from '../utils/api';
import { parseChatMarkdownEnabled } from '../utils/chatMarkdownSetting';
import { isPasswordResetPath, isPublicAuthPath } from '../utils/authRoutes';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [chatMarkdownEnabled, setChatMarkdownEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [backendError, setBackendError] = useState(null); // null | 'SERVER_UNAVAILABLE' | 'TIMEOUT' | 'AUTH_FAILED'

  // Initialize authentication state from Supabase
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Cache the token immediately if session exists
      if (session?.access_token) {
        const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
        updateCachedSessionToken(session.access_token, expiresAt);
        console.log('✅ Cached initial session token');
      }
      
      if (session?.user) {
        if (isPasswordResetPath(window.location.pathname)) {
          console.log('📝 Recovery session on reset-password page — skipping backend user load');
          setLoading(false);
        } else {
          console.log('📝 Initial session found, loading user...');
          loadUserFromSupabase(session.user);
        }
      } else {
        console.log('📝 No initial session, setting loading to false');
        setLoading(false);
      }
    }).catch((error) => {
      console.error('❌ Error getting initial session:', error);
      setLoading(false);
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth state changed:', event, session?.user?.email);
      
      // Update cached token immediately when session changes
      if (session?.access_token) {
        const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
        updateCachedSessionToken(session.access_token, expiresAt);
        console.log('✅ Updated cached session token from auth state change');
      } else {
        updateCachedSessionToken(null, null);
      }
      
      if (session?.user) {
        if (event === 'PASSWORD_RECOVERY' || isPasswordResetPath(window.location.pathname)) {
          console.log('🔐 Password recovery session — not loading backend user yet');
          setLoading(false);
        } else {
          await loadUserFromSupabase(session.user);
        }
      } else {
        setUser(null);
        setDarkMode(false);
        setChatMarkdownEnabled(true);
        setLoading(false);
        // Redirect to login when signed out (not on public auth pages)
        if (!isPublicAuthPath(window.location.pathname)) {
          import('../utils/config').then(({ getBasePath }) => {
            const basePath = getBasePath();
            const loginPath = `${window.location.origin}${basePath}/login`;
            window.location.href = loginPath;
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load user data from Supabase and sync with backend
  const loadUserFromSupabase = async (supabaseUser) => {
    // Always set loading to false at the end, even if there's an error
    let loadingSet = false;
    const ensureLoadingFalse = () => {
      if (!loadingSet) {
        loadingSet = true;
        setLoading(false);
      }
    };

    try {
      console.log('🔄 Loading user from backend API...');
      // fetchCurrentUser handles timeouts internally and returns { user, errorType }
      const { user: backendUser, errorType } = await fetchCurrentUser();
      console.log('📥 Backend result:', { email: backendUser?.email, errorType });

      if (backendUser && backendUser.email) {
        const darkModeValue = backendUser.darkMode;
        const shouldBeDark = darkModeValue === true ||
                           darkModeValue === 'true' ||
                           darkModeValue === 1 ||
                           darkModeValue === '1';
        const shouldRenderMarkdown = parseChatMarkdownEnabled(backendUser.chatMarkdownEnabled);

        const userData = {
          id: supabaseUser.id,
          email: backendUser.email || supabaseUser.email,
          accountType: backendUser.accountType || backendUser.user_type || 'user',
          darkMode: shouldBeDark,
          chatMarkdownEnabled: shouldRenderMarkdown,
          openaiKey: backendUser.openaiKey || null,
          geminiKey: backendUser.geminiKey || null,
          anthropicKey: backendUser.anthropicKey || null,
          currentActivePlatform: backendUser.currentActivePlatform || 'openai'
        };

        setUser(userData);
        setDarkMode(shouldBeDark);
        setChatMarkdownEnabled(shouldRenderMarkdown);
        setBackendError(null);
      } else if (errorType === 'AUTH_FAILED') {
        if (isPasswordResetPath(window.location.pathname)) {
          console.warn('🔒 Backend rejected token during password reset — keeping Supabase recovery session');
          setUser(null);
          setDarkMode(false);
          setBackendError(null);
        } else {
          console.warn('🔒 Auth rejected by backend. Signing out of Supabase...');
          try {
            await supabase.auth.signOut();
            updateCachedSessionToken(null, null);
          } catch (signOutError) {
            console.error('❌ Error signing out:', signOutError);
          }
          setUser(null);
          setDarkMode(false);
          setChatMarkdownEnabled(true);
          setBackendError(null);
        }
      } else {
        // SERVER_UNAVAILABLE or TIMEOUT — preserve the Supabase session so the user can retry
        console.warn('⚠️  Backend unavailable (', errorType, '). Keeping Supabase session for retry.');
        setUser(null);
        setDarkMode(false);
        setChatMarkdownEnabled(true);
        setBackendError(errorType || 'SERVER_UNAVAILABLE');
      }
    } catch (error) {
      // Unexpected error (fetchCurrentUser shouldn't throw, but just in case)
      console.error('❌ Unexpected error loading user from backend:', error);
      setUser(null);
      setDarkMode(false);
      setChatMarkdownEnabled(true);
      setBackendError('SERVER_UNAVAILABLE');
    } finally {
      console.log('✅ Setting loading to false');
      ensureLoadingFalse();
    }
  };

  // Set user from Supabase session (called after login)
  const setUserFromSupabase = async (supabaseUser) => {
    await loadUserFromSupabase(supabaseUser);
  };

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  }, [darkMode]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    if (data?.user) {
      await loadUserFromSupabase(data.user);
    }
    return data;
  };

  const logout = async () => {
    // Clear cached session token
    const { updateCachedSessionToken } = await import('../utils/api');
    updateCachedSessionToken(null, null);
    
    // Sign out from Supabase
    try {
      const { error } = await supabase.auth.signOut();
      // Only throw if it's NOT an AuthSessionMissingError
      // If session is already missing, we can proceed with cleanup
      if (error && error.name !== 'AuthSessionMissingError') {
        throw error;
      }
    } catch (error) {
      // If session is already missing, just log and continue with cleanup
      if (error.name === 'AuthSessionMissingError') {
        console.log('Session already cleared, proceeding with logout cleanup');
      } else {
        console.error('Error during Supabase signOut:', error);
        // Still continue with cleanup even if signOut fails
      }
    }
    
    // Clear local storage
    try {
      localStorage.removeItem('sb-auth-token');
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }
    
    // Clear user state
    setUser(null);
    setDarkMode(false);
    setChatMarkdownEnabled(true);
  };

  const toggleDarkMode = async () => {
    if (!user || !user.email) {
      console.error('❌ Cannot toggle dark mode: user not loaded');
      return;
    }

    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    const updatedUser = { ...user, darkMode: newDarkMode };
    setUser(updatedUser);

    try {
      await updateUserDarkMode(user.email, newDarkMode);
    } catch (error) {
      console.error('❌ Error updating dark mode:', error);
      // Revert on error
      setDarkMode(darkMode);
      setUser(user);
    }
  };

  const toggleChatMarkdown = async () => {
    if (!user || !user.email) {
      console.error('❌ Cannot toggle chat markdown: user not loaded');
      return;
    }

    const newValue = !chatMarkdownEnabled;
    setChatMarkdownEnabled(newValue);
    setUser({ ...user, chatMarkdownEnabled: newValue });

    try {
      await updateUserChatMarkdown(user.email, newValue);
    } catch (error) {
      console.error('❌ Error updating chat markdown preference:', error);
      setChatMarkdownEnabled(chatMarkdownEnabled);
      setUser(user);
    }
  };

  const retryLoadUser = async () => {
    setBackendError(null);
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await loadUserFromSupabase(session.user);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error('❌ Error retrying user load:', e);
      setLoading(false);
      setBackendError('SERVER_UNAVAILABLE');
    }
  };

  const value = {
    user,
    darkMode,
    chatMarkdownEnabled,
    loading,
    backendError,
    retryLoadUser,
    login,
    logout,
    toggleDarkMode,
    toggleChatMarkdown,
    setUserFromSupabase,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
